import { useIsMobile } from "@/hooks/use-mobile";
import {
  actionsKeys,
  cn,
  executionStatus,
  formatAmount,
  getMonthlyDayOptions,
  getRequiredFields,
  MONTHS,
  permissionActions,
  toApiSort,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { SEARCH_CRITERIA, SEARCH_TYPE, transactionTypeValues } from "../consts";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Label,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { BadgeInfo, MoreVertical } from "lucide-react";
import {
  useGetBillingCategoryByCommunity,
  useGetBillingConfigList,
  useGetExecutionByPricingId,
  useSetBillingConfigActiveInActiveMutation,
  useTriggerExecutionMutation,
  useUpdateStatusMutation,
} from "@/services";
import { frequencyOptions } from "../consts";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";

const useBillingConfig = () => {
  const isMobile = useIsMobile();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "updatedAt", desc: true }]);
  const [selectedPricingId, setSelectedPricingId] = useState<string>("");
  const [tableData, setTableData] = useState<any>([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openCategoryDialog, setOpenCategoryDialog] = useState<boolean>(false);
  const selectedBillingCategories = useRef<any>(null);

  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinancialYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const hasUpdatePermission = useMemo(() => accessPermission?.[actionsKeys?.BILLING_CONFIG?.toLowerCase()]?.[permissionActions.UPDATE] ,[accessPermission])
  const hasAddPermission = useMemo(() => accessPermission?.[actionsKeys?.BILLING_CONFIG?.toLowerCase()]?.[permissionActions.CREATE] ,[accessPermission])

  const { data: billingCategory, isLoading: isLoadingBillingCategory } =
  useGetBillingCategoryByCommunity({communityId: communityDetail?.communityId});

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.billing}`,
        name: "Billing",
      },
      {
        link: "",
        name: <span className="text-primary">Billing Configuration</span>,
      },
    ],
    []
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onAddBillingConfig = () => {
    navigate(`${routes.add}`);
  };

  const onRowClick = (row: any) => {
    navigate(`${row.original.pricingId}/view`);
  };

  const {
    data: configList,
    isLoading: isConfigLoading,
    refetch: refetchConfig,
    isRefetching: isConfigRefetching,
  } = useGetBillingConfigList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "budgetYear",
          value: currentFinancialYear,
          operator: "EQUAL",
        },
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator: "CONTAINS",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "updatedAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchConfig();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchConfig();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchConfig();
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchConfig(), 100);
  };
  useEffect(() => {
    if (configList) {
      setTableData(configList?.data || []);
    }
  }, [configList]);

  const { data: executionList } = useGetExecutionByPricingId({
    communityId: communityDetail?.communityId,
    pricingId: selectedPricingId,
    queryParams: {
      enabled: !!selectedPricingId,
    },
  });

  useEffect(() => {
    if (executionList) {
      const _tableData = [...tableData];
      const idx = _tableData?.findIndex(
        (val: any) => val.pricingId == selectedPricingId
      );
      _tableData[idx] = {
        ..._tableData[idx],
        children: executionList?.data?.sort(
          (a: any, b: any) =>
            new Date(b.billedDate).getTime() - new Date(a.billedDate).getTime()
        ),
      };
      setTableData([..._tableData]);
    }
  }, [executionList]);

  const { mutate: triggerExe, isPending: isExePending } =
    useTriggerExecutionMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : error?.response?.message
            ? error?.response?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        toast.success(
          data?.data?.message || "Execution Triggered Successfully"
        );
        queryClient.invalidateQueries({
          queryKey: [
            "billing-config-execution",
            communityDetail?.communityId,
            selectedPricingId,
          ],
        });
      },
    });

  const onTriggerExecution = (exeId: string) => {
    triggerExe({
      pricingId: selectedPricingId as string,
      communityId: communityDetail?.communityId as string,
      id: exeId as string,
    });
  };

  const { mutate: updateStatus, isPending: isUpdateStatusPending } =
    useUpdateStatusMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Status Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: [
            "billing-config-execution",
            communityDetail?.communityId,
            selectedPricingId,
          ],
        });
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "billingCategoryIds",
      header: <div>Billing Category</div>,
      cell: ({row}: any) => {
        const billingId = row.original.billingCategoryIds?.split(",");

        if (billingId?.length == 1) {
          return billingId?.map((data: any) => {
            return (
              billingCategory?.data?.find(
                (val: any) =>
                  data?.trim()?.toString() == val.billingCategoryId?.toString()
              )?.categoryName || ""
            );
          })
        } else if (!billingId?.length || billingId?.length == billingCategory?.data?.length) {
          return <Button variant="link" className="px-0" onClick={() => {
            selectedBillingCategories.current = billingCategory?.data?.map((data: any) => data?.billingCategoryId?.toString());
            setOpenCategoryDialog(true);
          }}>All</Button>
        } else {
          return <Button variant="link" className="px-0" onClick={() => {
            selectedBillingCategories.current = billingId;
            setOpenCategoryDialog(true);
          }}>Multiple</Button>
        }
      },
      size: 50,
    },
    {
      accessorKey: "glAccountNumber",
      header: <div>GL Account</div>,
      accessorFn: (row: any) => `${row?.glAccountNumber} - ${row?.glAccountDescription}`,
    },
    {
      accessorKey: "transactionType",
      header: <div>Transaction Type</div>,
      accessorFn: (row: any) =>
        (transactionTypeValues as any)[String(row?.transactionType || "")],
    },
    {
      accessorKey: "frequency",
      header: <div>Frequency</div>,
      accessorFn: (row: any) =>
        String(
          frequencyOptions?.find((data: any) => data?.value == row?.frequency)
            ?.label
        ) || "",
    },
    {
      accessorKey: "amount",
      meta: { align: "right" },
      header: <div>Payment Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.amount || 0),
    },
    {
      accessorKey: "billingRunDate",
      header: <div>Billing Date</div>,
      accessorFn: (row: any) => {
        return String(row?.transactionType || "")?.toUpperCase() ==
          "MONTHLY_HOA_FEE" && row?.nextBillingDate
          ? getMonthlyDayOptions(
              Number(moment(row?.nextBillingDate).format("DD"))
            )
          : row?.billingRunDate
          ? moment(row?.billingRunDate || "").format("MM/DD/YYYY")
          : "";
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              row.row.original.active
                ? "border-green-600 bg-green-100 text-green-600"
                : "border-red-600 bg-red-100 text-red-600",
              "rounded-full px-4 py-2"
            )}
          >
            {row.row.original.active ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    ...(hasUpdatePermission
      ? [
          {
            id: "actions",
            header: "Actions",
            enableSorting: false,
            cell: (row: any) => {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <MoreVertical className="w-5 h-5 cursor-pointer" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-white border-0 shadow-md"
                  >
                    <DropdownMenuLabel className="flex items-center gap-2">
                      <div
                        className="flex items-center space-x-2"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <Switch
                          id="active"
                          onCheckedChange={(isActive: any) => {
                            onSetActiveConfig(
                              isActive,
                              row?.row?.original?.pricingId
                            );
                          }}
                          checked={row.row.original.active}
                          disabled={isAddPending}
                        />
                        <Label htmlFor="active">
                          {row.row.original.active ? "Active" : "Inactive"}
                        </Label>
                      </div>
                    </DropdownMenuLabel>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]
      : []),
  ];

  const subColumns = [
    {
      accessorKey: "pricingExecutionId",
      header: <div>Execution ID</div>,
    },
    {
      accessorKey: "executionDate",
      header: <div>Execution Date</div>,
      accessorFn: (row: any) =>
        row?.executionDate
          ? moment(row?.executionDate).format("MM/DD/YYYY")
          : "",
    },
    {
      accessorKey: "billingPeriod",
      header: <div>Billing Period</div>,
      accessorFn: (row: any) => MONTHS[row.billingPeriod - 1],
    },
    {
      accessorKey: "billingDate",
      header: <div>Billing Date</div>,
      accessorFn: (row: any) =>
        row.billingDate ? moment(row.billedDate).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "postingDate",
      header: <div>Posting Date</div>,
      accessorFn: (row: any) =>
        row.postingDate ? moment(row.postingDate).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "dueDate",
      header: <div>Due Date</div>,
      accessorFn: (row: any) =>
        row.dueDate ? moment(row.dueDate).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "executionStatus",
      header: "Status",
      cell: (row: any) => {
        const isActive = configList?.data?.find(
          (data: any) => data?.pricingId == row.pricingId
        )?.active;

        return (
          <>
            <Badge
              variant="outline"
              className={cn(
                (executionStatus as any)[
                  isActive ? row?.executionStatus?.toLowerCase() : "inactive"
                ]?.color,
                "rounded-full px-4 py-2"
              )}
            >
              {
                (executionStatus as any)[
                  isActive ? row?.executionStatus?.toLowerCase() : "inactive"
                ]?.label
              }
            </Badge>
            {row?.executionStatus?.toLowerCase() == "failed" ?
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="mx-1 mt-0" type="button">
                  <BadgeInfo className="h-5 w-5 stroke-amber-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col text-[14px] text-primary p-3">
                        {row?.errorMessage || ""}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            : <></>}
          </>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: (row: any) => {
        const isActive = configList?.data?.find(
          (data: any) => data?.pricingId == row.pricingId
        )?.active;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className={cn(
                  "flex items-center gap-2",
                  isExePending ||
                    row?.executionStatus?.toLowerCase() == "completed"
                    ? "hover:cursor-not-allowed"
                    : ""
                )}
                disabled={
                  isExePending ||
                  !isActive ||
                  row?.executionStatus?.toLowerCase() == "completed"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onTriggerExecution(row?.pricingExecutionId);
                }}
              >
                Run Job
              </DropdownMenuItem>
              <DropdownMenuLabel className="flex items-center gap-2">
                <div
                  className="flex items-center space-x-2"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Switch
                    id="active"
                    onCheckedChange={(isActive: any) => {
                      updateStatus({
                        pricingId: row?.pricingId,
                        communityId: communityDetail?.communityId,
                        id: row?.pricingExecutionId,
                        data: {
                          status: isActive
                            ? executionStatus.cancelled.value
                            : executionStatus.pending.value,
                        },
                      });
                    }}
                    checked={
                      row?.executionStatus != executionStatus.cancelled.value
                    }
                    disabled={isUpdateStatusPending}
                  />
                  <Label htmlFor="active">
                    {row?.executionStatus == executionStatus.cancelled.value
                      ? "Inactive"
                      : "Active"}
                  </Label>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const { mutate: addInfo, isPending: isAddPending } =
    useSetBillingConfigActiveInActiveMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Billing Configuration Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: ["billing-config-list"],
        });
      },
    });

  const onSetActiveConfig = (isActive: boolean, pricingId: string) => {
    addInfo({
      communityId: communityDetail?.communityId,
      pricingId,
      isActive,
    });
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}/${routes.billing}`);
  };

  const onRowExpanded = (row: any) => {
    // not to call when row is collapsible
    if (!row.getIsExpanded()) {
      if (selectedPricingId == row?.original?.pricingId) {
        setSelectedPricingId("");
      }
      setTimeout(() => setSelectedPricingId(row?.original?.pricingId), 100);
    }
  };

  return {
    breadcrumbData,
    communityDetail,
    isMobile,
    onAddBillingConfig,
    setGlobalFilter,
    globalFilter,
    onRowClick,
    form,
    onSubmit,
    requiredFields,
    columns,
    onClear,
    isConfigLoading,
    isConfigRefetching,
    configList,
    billingCategory,
    isLoadingBillingCategory,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    onBackClick,
    subColumns,
    tableData,
    onRowExpanded,
    currentFinancialYear,
    openCategoryDialog,
    setOpenCategoryDialog,
    selectedBillingCategories: selectedBillingCategories.current,
    hasAddPermission,
    sorting,
    setSorting,
  };
};

export default useBillingConfig;
